import React, { FunctionComponent, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  getPaymentState,
  getProductDescription,
  getPaymentWhen,
} from 'scenes/payments/redux/payment/selectors';
import { PolicySummary } from 'scenes/common/components/PolicySummary';
import styled from 'styled-components';
import { Text } from 'components/chroma';
import AccountCreated from 'scenes/common/components/Steps/Payments/PaymentConfirmationStep/AccountCreated';
import { InsuranceList } from 'scenes/common/components/Steps/Payments/PaymentConfirmationStep/InsuranceList';
import { WireProduct } from 'scenes/common/api/quote/types';
import { InsuranceName, PaymentMethod, PaymentWhen } from 'scenes/common/types';
import { RenewalStatus } from 'scenes/payments/types';
import { getPolicyStartAndEndDate } from 'scenes/payments/helpers/utils';
import { useHistory } from 'react-router';
import { History } from 'history';
import { onBackGoToLanding } from 'scenes/payments/routes';
import { AnotherPayment } from './AnotherPayment';

const Container = styled.div`
  margin-top: 4rem;
`;

const getInsuranceName = (product: WireProduct): InsuranceName => {
  switch (product) {
    case WireProduct.Motor:
      return InsuranceName.Car;
    case WireProduct.Home:
      return InsuranceName.Home;
    case WireProduct.Landlord:
    default:
      return InsuranceName.Landlord;
  }
};

type MessageDataType = {
  renewalStatus: RenewalStatus;
  paymentType: PaymentMethod;
  emailAddressProvided?: boolean;
  message: JSX.Element;
  futureDated?: boolean;
};

const messageData = (receipt?: string): Array<MessageDataType> => [
  {
    renewalStatus: RenewalStatus.Renewal,
    paymentType: PaymentMethod.DirectDebit,
    futureDated: false,
    message: (
      <Text>
        Once your payment has been processed, we will send you a copy of your
        receipt.
      </Text>
    ),
  },
  {
    renewalStatus: RenewalStatus.Renewal,
    paymentType: PaymentMethod.CreditCard,
    futureDated: false,
    message: (
      <Text>
        Your receipt number is {receipt}. We will send you a copy of your
        receipt.
      </Text>
    ),
  },
  {
    renewalStatus: RenewalStatus.Renewal,
    paymentType: PaymentMethod.DirectDebit,
    futureDated: true,
    message: (
      <Text>
        Your payment will be processed within 3 business days of your nominated
        payment date of your renewal. We will send you a copy of your receipt.
      </Text>
    ),
  },
  {
    renewalStatus: RenewalStatus.Renewal,
    paymentType: PaymentMethod.CreditCard,
    futureDated: true,
    message: (
      <Text>
        Your payment will be processed within 3 business days of your nominated
        payment date of your renewal. We will send you a copy of your receipt.
      </Text>
    ),
  },
  {
    renewalStatus: RenewalStatus.Dishonour,
    paymentType: PaymentMethod.DirectDebit,
    emailAddressProvided: true,
    message: (
      <Text>
        Once your payment has been processed, we will email you a copy of your
        receipt.
      </Text>
    ),
  },
  {
    renewalStatus: RenewalStatus.Dishonour,
    paymentType: PaymentMethod.DirectDebit,
    emailAddressProvided: false,
    message: <Text>Your payment will be processed.</Text>,
  },
  {
    renewalStatus: RenewalStatus.Dishonour,
    paymentType: PaymentMethod.CreditCard,
    emailAddressProvided: true,
    message: (
      <Text>
        Your receipt number is {receipt}. We will email you a copy of your
        receipt.
      </Text>
    ),
  },
  {
    renewalStatus: RenewalStatus.Dishonour,
    paymentType: PaymentMethod.CreditCard,
    emailAddressProvided: false,
    message: (
      <Text>
        Your receipt number is {receipt}. We will email you a copy of your
        receipt.
      </Text>
    ),
  },
];

// Don't check the field if either the query or candidate
// values are null
const skipFieldMatch = (queryValue: unknown, candidateValue: unknown) =>
  queryValue === undefined || candidateValue === undefined;

// To confirm that renewal status maps to 'one off'
// and 'renewal' payments
const getNextActionMessage = (
  renewalStatus: RenewalStatus,
  email?: string,
  paymentType?: PaymentMethod,
  receipt?: string,
  futureDated?: boolean
): JSX.Element | undefined => {
  const emailProvided = !!email;
  // Find message with same renewal status, payment type
  // and email provided criteria (if required)
  const correctMessage = messageData(receipt).find(
    (candidate) =>
      candidate.renewalStatus === renewalStatus &&
      candidate.paymentType === paymentType &&
      (skipFieldMatch(emailProvided, candidate.emailAddressProvided) ||
        candidate.emailAddressProvided === emailProvided) &&
      (skipFieldMatch(futureDated, candidate.futureDated) ||
        candidate.futureDated === futureDated)
  );
  return correctMessage?.message;
};

export const Confirmation: FunctionComponent = () => {
  const paymentState = useSelector(getPaymentState);
  const dates = getPolicyStartAndEndDate(paymentState, 'D MMM YYYY');
  const history: History = useHistory();

  const paymentAtDueDate = useSelector(getPaymentWhen) === PaymentWhen.DueDate;

  const isFuturePayment = (dueDate?: string) =>
    dueDate ? paymentAtDueDate && new Date(dueDate) > new Date() : false;

  useEffect(() => {
    onBackGoToLanding(history);
  }, [history]);

  return (
    <>
      <Container>
        <PolicySummary
          policyNumber={paymentState.policy.number}
          policyStartDate={dates.startDate}
          policyExpiryDate={dates.endDate}
          productType={getProductDescription({ payment: paymentState })}
          headingSize={2}
        />
        {getNextActionMessage(
          paymentState.policy.renewalStatus,
          paymentState.payment.contactEmailAddress,
          paymentState.payment.paymentMethod,
          paymentState.payment.receipt,
          isFuturePayment(paymentState.policy.dates?.effective)
        )}
        <Text>
          Note that the payment will appear under the name of{' '}
          <strong>Insurance Australia Limited (IAL)</strong> on your financial
          institution statement.
        </Text>
      </Container>
      <AccountCreated />
      <AnotherPayment />
      <InsuranceList
        insuranceName={getInsuranceName(paymentState.product)}
        paymentPortal
      />
    </>
  );
};

export default Confirmation;
